"use client";

import { Link } from "@/src/lib/next-compat";
import { useNavigate } from "react-router-dom";
import { Clock, PlayCircle, CheckCircle, Settings } from "lucide-react";
import EnrollButton from "@/components/student/EnrollButton";
import type { CourseCard as CourseCardData } from "@/lib/types/course";
import { useCourseNavigation } from "@/lib/contexts/CourseNavigationContext";

type CourseCardProps = {
    course: CourseCardData;
    onCourseClick: (e: React.MouseEvent, course: CourseCardData) => void;
};

export default function CourseCard({ course, onCourseClick }: CourseCardProps) {
    const navigate = useNavigate();
    const nav = useCourseNavigation();
    const courseParam = course.uuid || course.id;

    // Having taken placement already (`placementDetermined`) doesn't mean the user
    // has committed to a level - only actual enrollment/purchase/progress does. Keep
    // that out of this flag so the card still routes them to the retake/enroll
    // flow instead of a premature "Start Course" link.
    const isUserEnrolledOrPurchased =
        Boolean(course.isEnrolled) ||
        Boolean(course.hasPurchasedCourse) ||
        (course.progress ?? 0) > 0;

    const canTakePlacementTest =
        Boolean(course.hasPlacementQuiz) &&
        !isUserEnrolledOrPurchased;

    // Retake is only offered from the course detail page - the catalog card
    // only ever prompts a first-time take.
    const showPlacementTestCta = canTakePlacementTest && !course.placementDetermined;

    return (
        <div
            className="group relative bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:border-indigo-200 transition-all duration-300 flex flex-col"
        >
            <Link
                to={`/courses/${courseParam}`}
                prefetch
                className="absolute inset-0 z-10"
                tabIndex={-1}
                aria-hidden="true"
                onClick={(e: React.MouseEvent) => onCourseClick(e, course)}
            />

            <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden">
                <img src={course.cover_image_url || "/images/courses/python-ai.png"}
                    alt={course.title}
                    onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "/images/courses/python-ai.png";
                    }}
                    className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                {(course.organizationName || course.accessMode === "SPONSORED") && (
                    <div className="absolute top-2 sm:top-3 right-2 sm:right-3 bg-indigo-600/90 backdrop-blur-sm px-2 py-1 rounded text-[10px] font-bold text-white uppercase tracking-wider shadow-sm">
                        {course.organizationName || "SPONSORED"}
                    </div>
                )}

                {course.completed && (
                    <div className="absolute inset-0 bg-green-900/40 flex items-center justify-center backdrop-blur-[1px]">
                        <div className="bg-white/90 text-green-700 px-3 sm:px-4 py-2 rounded-full font-bold text-xs sm:text-sm flex items-center gap-2 shadow-lg">
                            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 fill-green-100" /> Completed
                        </div>
                    </div>
                )}
            </div>

            <div className="p-4 sm:p-6 flex-1 flex flex-col">
                <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 group-hover:text-indigo-600 transition-colors line-clamp-2">
                        {course.title}
                    </h3>
                    <Link
                        to={`/settings?courseId=${encodeURIComponent(courseParam)}`}
                        className="relative z-20 ml-3 inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition-colors hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-600"
                        aria-label={`Open course settings for ${course.title}`}
                        title="Course settings"
                    >
                        <Settings className="h-4 w-4" />
                    </Link>
                </div>

                {!course.completed && isUserEnrolledOrPurchased && course.progress > 0 && (
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mb-3 sm:mb-4">
                        <div
                            className="bg-indigo-600 h-1.5 rounded-full transition-all duration-500"
                            style={{ width: `${course.progress}%` }}
                        />
                        <p className="text-xs text-indigo-600 font-bold mt-1 text-right">{course.progress}% Complete</p>
                    </div>
                )}

                <p className="text-slate-600 text-sm leading-relaxed mb-4 sm:mb-6 flex-1 line-clamp-3">
                    {course.description}
                </p>

                <div className="mt-auto relative z-20">
                    <div className="flex items-center gap-2 text-xs text-slate-500 font-medium mb-3 sm:mb-4">
                        <Clock className="w-4 h-4" />
                        {typeof course.lessons === "number" ? `${course.lessons} lessons` : ""}
                        {course.level && course.level !== "Not determined yet"
                            ? ` • ${course.level}`
                            : showPlacementTestCta
                            ? " • Placement pending"
                            : ""}
                    </div>

                    {course.completed ? (
                        <Link
                            to={`/courses/${courseParam}`}
                            prefetch
                            className="block w-full py-2.5 sm:py-3 text-center rounded-xl border border-slate-200 font-semibold text-sm text-slate-700 hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-all active:scale-[0.98]"
                            onClick={(e: React.MouseEvent) => onCourseClick(e, course)}
                        >
                            Review Course
                        </Link>
                    ) : isUserEnrolledOrPurchased ? (
                        <Link
                            prefetch
                            to={`/courses/${courseParam}`}
                            className={`block w-full py-2.5 sm:py-3 text-center rounded-xl font-semibold text-sm transition-all active:scale-[0.98] ${course.progress > 0
                                ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md hover:shadow-lg'
                                : 'border border-gray-200 text-gray-700 hover:bg-indigo-600 hover:text-white hover:border-indigo-600'
                                }`}
                            onClick={(e: React.MouseEvent) => onCourseClick(e, course)}
                        >
                            {course.progress > 0 ? (
                                <span className="flex items-center justify-center gap-2">
                                    <PlayCircle className="w-4 h-4" /> Resume
                                </span>
                            ) : (
                                "Start Course"
                            )}
                        </Link>
                    ) : course.accessMode === "SPONSORED" ? (
                        <Link
                            prefetch
                            to={`/courses/${courseParam}`}
                            className={`block w-full py-2.5 sm:py-3 text-center rounded-xl font-semibold text-sm transition-all active:scale-[0.98] ${course.progress > 0
                                ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md hover:shadow-lg'
                                : 'border border-slate-200 text-slate-700 hover:bg-indigo-600 hover:text-white hover:border-indigo-600'
                                }`}
                            onClick={(e: React.MouseEvent) => onCourseClick(e, course)}
                        >
                            {course.progress > 0 ? (
                                <span className="flex items-center justify-center gap-2">
                                    <PlayCircle className="w-4 h-4" /> Resume
                                </span>
                            ) : (
                                "Start Course"
                            )}
                        </Link>
                    ) : course.hasActiveSubscription ? (
                        <div className="space-y-2">
                            {showPlacementTestCta && (
                                <Link
                                    to={`/placement-quiz/${courseParam}`}
                                    className="block w-full py-2 text-center rounded-xl font-semibold text-xs text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-all border border-indigo-200"
                                >
                                    Take Placement Test
                                </Link>
                            )}
                            <EnrollButton
                                courseId={courseParam}
                                courseName={course.title}
                                canEnrollNow={course.canEnrollNow ?? false}
                                nextEnrollmentAt={course.nextEnrollmentAt ?? null}
                            />
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {course.hasPlacementQuiz ? (
                                <div className="grid grid-cols-2 gap-2">
                                    <Link
                                        to={`/courses/${courseParam}`}
                                        prefetch
                                        className="block w-full py-2.5 sm:py-3 text-center rounded-xl font-semibold text-sm border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all active:scale-[0.98]"
                                        onClick={(e: React.MouseEvent) => onCourseClick(e, course)}
                                    >
                                        View Course
                                    </Link>
                                    <Link
                                        to={`/placement-quiz/${courseParam}`}
                                        className="block w-full py-2.5 sm:py-3 text-center rounded-xl font-semibold text-sm bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-all active:scale-[0.98]"
                                    >
                                        Placement Test
                                    </Link>
                                </div>
                            ) : (
                                <Link
                                    to={`/courses/${courseParam}`}
                                    prefetch
                                    className="block w-full py-2.5 sm:py-3 text-center rounded-xl font-semibold text-sm border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all active:scale-[0.98]"
                                    onClick={(e: React.MouseEvent) => onCourseClick(e, course)}
                                >
                                    View Course
                                </Link>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
